import { useRef, useMemo, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions, selectors, SectionNames, isEmptyField } from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import { RichTextEditorInstanceWrapper } from 'builder/components/RichTextArea/types'
import { useConfig } from 'builder/hooks/useConfig'
import {
  AiAssistantProcessing,
  AiProfileSummary,
  GenerateWithTailoredRole,
  AIProfileSummaryResult,
} from 'builder/components/AiFeatures/AiProfileSummary'
import {
  PhraseData,
  PhrasesSuggestion,
  SuggestionsToggler,
  SUGGESTIONS_CHECK_KEY,
  ToggleSuggestionsParams,
} from 'builder/components/PhrasesSuggestion'
import { isEmptySection } from 'builder/modules/resumeEditor/aiProfileRules'
import { useTourConfig } from '../../DocumentEditor/DocumentTour/hooks/useTourConfig'

type Props = {
  editorInstanceRef: React.RefObject<RichTextEditorInstanceWrapper>
  editorContainerRef: React.RefObject<HTMLDivElement>
  isStepByStepFlow?: boolean
}

function shouldUseAiProfileSummary(locale: string) {
  return ['en', 'en-GB'].includes(locale)
}

export const useProfileSummarySuggestions = (props: Props) => {
  const { editorInstanceRef, editorContainerRef, isStepByStepFlow } = props
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const { onChangePhrasesSuggestions } = useTourConfig()
  const suggestionLocales = useTypedSelector(state => state.generalEditor.suggestionLocales)
  const resume = useTypedSelector(selectors.resume)
  const {
    summary: generatedContent,
    generateAiSummaryStatus,
    isWithTailoredResumeModalOpen,
  } = useTypedSelector(selectors.aiProfile)
  const { locale = '', position } = resume || {}
  const isProfileEmpty = isEmptyField(resume, SectionNames.profile)
  const config = useConfig()
  const isAiProfileAvailable = config?.features.aiProfileSummary || false
  const isAiProfileSummary = shouldUseAiProfileSummary(locale)
  const isSuggestionsEnabled = suggestionLocales.profile?.includes(locale)
  const isWorkExperienceEmpty = isEmptySection(resume, SectionNames.workExperiences)
  const shouldUseAiProfileSuggestions =
    isAiProfileAvailable && isAiProfileSummary && !(isStepByStepFlow && isWorkExperienceEmpty)
  const isAiProfileSummaryModalOpen = useTypedSelector(selectors.isAiProfileSummaryModalOpen)
  const isAIProfileSummaryResultOpen = useTypedSelector(selectors.isAIProfileSummaryResultOpen)
  const phrasesOpenerRef = useRef<HTMLDivElement>(null)
  const phrasesSuggestionRef = useRef<HTMLDivElement>(null)
  const aiProfileSummaryResultRef = useRef<HTMLDivElement>(null)
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false)

  const onToggleSuggestions = useCallback(
    (state: ToggleSuggestionsParams) => {
      const { isOpen, trigger } = state
      const newState = isOpen || !isSuggestionsOpen

      setIsSuggestionsOpen(newState)

      if (newState) {
        dispatch(actions.setIsAiProfileSummaryModalOpen({ status: false }))
        trackInternalEvent('open_pre_written_phrases_dialog', {
          section: SectionNames.profile,
          trigger,
        })
      }
    },
    [dispatch, isSuggestionsOpen],
  )

  const onFocus = useCallback(() => {
    const isTunerTipsEnabled = window.location.search.includes('tuner_tips')
    const storedSuggestions = localStorage.getItem(SUGGESTIONS_CHECK_KEY)
    // Additional check to avoid JSON parsing error
    const parsedSuggestionsState = storedSuggestions ? JSON.parse(storedSuggestions) : null
    const suggestionsState = Array.isArray(parsedSuggestionsState) ? parsedSuggestionsState : []
    const suggestionsWereShown = suggestionsState.includes(SectionNames.profile)

    if (!suggestionsWereShown && !isTunerTipsEnabled) {
      if (shouldUseAiProfileSuggestions) {
        dispatch(actions.setIsAiProfileSummaryModalOpen({ status: !isAiProfileSummaryModalOpen }))
      } else {
        onToggleSuggestions({ isOpen: true, trigger: 'focus_field' })
      }
      suggestionsState.push(SectionNames.profile)
      const updatedSuggestionState = suggestionsState.filter(Boolean)
      if (updatedSuggestionState.length > 0) {
        localStorage.setItem(SUGGESTIONS_CHECK_KEY, JSON.stringify(updatedSuggestionState))
      }
    }
  }, [dispatch, isAiProfileSummaryModalOpen, onToggleSuggestions, shouldUseAiProfileSuggestions])

  const additionalToolbarActions = useMemo(() => {
    if (!isSuggestionsEnabled) {
      return null
    }

    const onToggleAiProfileSummary = () => {
      if (!isSuggestionsOpen && !isAIProfileSummaryResultOpen && !isAiProfileSummaryModalOpen) {
        trackInternalEvent('click_writing_help', {
          section: 'profile',
          profile_empty: isProfileEmpty,
        })
      }

      if (isSuggestionsOpen) {
        setIsSuggestionsOpen(false)
        onChangePhrasesSuggestions()
        return
      }

      if (generatedContent) {
        dispatch(actions.setIsAIProfileSummaryResultOpen({ status: true }))
        dispatch(actions.setAIProfileResultContentAnimation(false))
      } else {
        if (isAiProfileSummaryModalOpen) {
          onChangePhrasesSuggestions()
        }
        dispatch(actions.setIsAiProfileSummaryModalOpen({ status: !isAiProfileSummaryModalOpen }))
      }
    }

    const onAddPhrase = (phrase: PhraseData) => {
      editorInstanceRef.current?.addPhrase(phrase, false)
      onChangePhrasesSuggestions()
    }

    return (
      <>
        {shouldUseAiProfileSuggestions ? (
          <>
            <SuggestionsToggler
              isActive={
                isAiProfileSummaryModalOpen || isAIProfileSummaryResultOpen || isSuggestionsOpen
              }
              ref={phrasesOpenerRef}
              onToggle={onToggleAiProfileSummary}
              text={i18n.t('builder.ai_profile_summary.help_with_writing_modal.button_text')}
              isAiAction={true}
            />
          </>
        ) : (
          <>
            <SuggestionsToggler
              isActive={isSuggestionsOpen}
              ref={phrasesOpenerRef}
              onToggle={() => onToggleSuggestions({ trigger: 'click' })}
              isAiAction={false}
            />
          </>
        )}
        {isSuggestionsOpen && (
          <PhrasesSuggestion
            text={editorInstanceRef.current?.getEditorText() || ''}
            onSelect={onAddPhrase}
            locale={locale}
            outsideOpenerRef={phrasesOpenerRef}
            scope={SectionNames.profile}
            elementRef={phrasesSuggestionRef}
            editorContainerRef={editorContainerRef}
            onToggleSuggestions={onToggleSuggestions}
            jobTitle={position || ''}
          />
        )}
        {isAiProfileSummaryModalOpen && !generatedContent && shouldUseAiProfileSuggestions && (
          <AiProfileSummary {...{ phrasesOpenerRef, editorContainerRef, onToggleSuggestions }} />
        )}
        {isAIProfileSummaryResultOpen && shouldUseAiProfileSuggestions && (
          <AIProfileSummaryResult
            ref={aiProfileSummaryResultRef}
            {...{
              phrasesOpenerRef,
              editorContainerRef,
            }}
          />
        )}
        <AiAssistantProcessing
          sectionNameForAnalytics={SectionNames.profile}
          status={generateAiSummaryStatus}
          onBackClick={() => {
            dispatch(actions.resetGenerateAiProfileSummaryStatus())
            dispatch(actions.setIsAiProfileSummaryModalOpen({ status: true }))
          }}
          onTryAgainClick={() => {
            dispatch(actions.generateResumeAiProfileSummary())
          }}
        />
        {isWithTailoredResumeModalOpen && (
          <GenerateWithTailoredRole section={SectionNames.profile} />
        )}
      </>
    )
  }, [
    dispatch,
    editorContainerRef,
    editorInstanceRef,
    generatedContent,
    i18n,
    isAIProfileSummaryResultOpen,
    isAiProfileSummaryModalOpen,
    isProfileEmpty,
    isWithTailoredResumeModalOpen,
    isSuggestionsOpen,
    locale,
    shouldUseAiProfileSuggestions,
    isSuggestionsEnabled,
    position,
    generateAiSummaryStatus,
    onToggleSuggestions,
    onChangePhrasesSuggestions,
  ])

  return {
    additionalToolbarActions,
    onFocus,
  }
}
